import React, { useEffect, useState } from 'react'
import { Card, Row, Col, Statistic, Button, Select, Modal, message, Pagination, Grid, Dropdown } from 'antd'
import { BookOutlined, ImportOutlined, SearchOutlined, AppstoreAddOutlined, ShareAltOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { computeApiBaseURL, request } from '../utils/request'
import { useAuthStore } from '../stores/auth'
import { buildSiteSharePayload, shareContent } from '@/utils/share'
import ContinueReadingSection from '../components/ContinueReadingSection'
import { getContinueReadingPage, trackContinueReadingClick } from '../api/home'
import type { ContinueReadingItem } from '../types/home'
import CategoryExhibitionSection from '../components/home/CategoryExhibitionSection'
import HomeBookCard from '../components/home/HomeBookCard'
import HomeSectionState from '../components/home/HomeSectionState'
import { useFontScale, type FontScaleMode } from '../contexts/FontScaleContext'
import AuthorClaimModal from '../components/AuthorClaimModal'

interface Book {
  id: string
  title: string
  author?: string
  format: string
  size: string
  uploadTime: string
}

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}

type PublicRecItem = {
  id: number
  name: string
  author: string
  format: string
  source: 'strong' | 'rating'
  avg_rating?: number
  rating_count?: number
}

const buildFallbackContinueReadingItems = (items: PublicRecItem[]): ContinueReadingItem[] => {
  return (Array.isArray(items) ? items : []).slice(0, 30).map((item, index) => ({
    book_id: Number(item.id),
    cover: getBookCoverUrl(Number(item.id)),
    title: item.name,
    author: item.author || '未知作者',
    read_progress_percent: 0,
    remaining_minutes: 0,
    hot_tag: `全站热度 Top${Math.max(1, Math.ceil(((index + 1) / Math.max(1, items.length || 1)) * 100))}%`,
    reason: item.source === 'strong' ? '编辑精选热门好书' : '根据全站热度为你推荐',
    fallback: true,
  }))
}

const getBookCoverUrl = (bookId: number) => {
  try {
    return new URL(`/api/books/${bookId}/cover`, computeApiBaseURL()).toString()
  } catch {
    return `/api/books/${bookId}/cover`
  }
}

const Home: React.FC = () => {
  const navigate = useNavigate()
  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md
  const { fontScale, setFontScale } = useFontScale()
  const recommendationSwipeRef = React.useRef<{ x: number; y: number } | null>(null)
  const [stats, setStats] = useState<{ books: number; today: number }>({ books: 0, today: 0 })
  const [recentBooks, setRecentBooks] = useState<Book[]>([])
  const [recentBooksError, setRecentBooksError] = useState('')
  const [sortField, setSortField] = useState<'title' | 'author' | 'format' | 'size' | 'uploadTime'>('uploadTime')
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend'>('descend')
  const getFieldValue = (o: Book, k: 'title' | 'author' | 'format' | 'size' | 'uploadTime'): string => {
    if (k === 'title') return String(o.title || '')
    if (k === 'author') return String(o.author || '')
    if (k === 'format') return String(o.format || '')
    if (k === 'size') return String(o.size || '')
    return String(o.uploadTime || '')
  }
  const sortedRecentBooks = React.useMemo(() => {
    const arr = [...recentBooks]
    const cmp = (a: Book, b: Book) => {
      const dir = sortOrder === 'ascend' ? 1 : -1
      if (sortField === 'size') {
        const pa = parseFloat(String(a.size || '').replace(/[^0-9.]/g, '')) || 0
        const pb = parseFloat(String(b.size || '').replace(/[^0-9.]/g, '')) || 0
        return dir * (pa - pb)
      }
      if (sortField === 'uploadTime') {
        return dir * (new Date(a.uploadTime).getTime() - new Date(b.uploadTime).getTime())
      }
      const va = getFieldValue(a, sortField)
      const vb = getFieldValue(b, sortField)
      return dir * va.localeCompare(vb)
    }
    arr.sort(cmp)
    return arr
  }, [recentBooks, sortField, sortOrder])
  const [publicRecs, setPublicRecs] = useState<PublicRecItem[]>([])
  const [publicRecsPage, setPublicRecsPage] = useState<number>(1)
  const [publicRecsPageSize, setPublicRecsPageSize] = useState<number>(24)
  const [hotCount, setHotCount] = useState<number>(0)
  const [continueReadingItems, setContinueReadingItems] = useState<ContinueReadingItem[]>([])
  const [continueReadingPage, setContinueReadingPage] = useState<number>(0)
  const [continueReadingHasMore, setContinueReadingHasMore] = useState<boolean>(false)
  const [continueReadingLoading, setContinueReadingLoading] = useState<boolean>(false)
  const [continueReadingLoadingMore, setContinueReadingLoadingMore] = useState<boolean>(false)
  const [continueReadingFallback, setContinueReadingFallback] = useState<boolean>(false)

  const { user, isAuthenticated } = useAuthStore()
  const [canUpload, setCanUpload] = useState<boolean>(false)
  const [recentImportVisible, setRecentImportVisible] = useState<boolean>(false)
  const [siteName, setSiteName] = useState('伴读么 - Ai角色朗读电子书屋')
  const [marqueeContent, setMarqueeContent] = useState('')
  const marqueeContainerRef = React.useRef<HTMLDivElement | null>(null)
  const marqueeMeasureRef = React.useRef<HTMLSpanElement | null>(null)
  const [marqueeRepeat, setMarqueeRepeat] = React.useState(2)
  const [marqueeShiftPx, setMarqueeShiftPx] = React.useState(0)
  const [marqueeDurationSec, setMarqueeDurationSec] = React.useState(30)
  const [marqueeGapPx, setMarqueeGapPx] = React.useState(64)
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [sharingSite, setSharingSite] = useState(false)
  const [resumePath, setResumePath] = useState('')
  const [resumeAt, setResumeAt] = useState(0)
  const [authorClaimOpen, setAuthorClaimOpen] = useState(false)
  const role = user?.role || 'guest'
  const canViewRecentImports = isAuthenticated && (
    role === 'admin' ||
    role === 'super_admin' ||
    recentImportVisible
  )
  const fontScaleOptions: Array<{ value: FontScaleMode; label: string }> = React.useMemo(() => ([
    { value: 'small', label: '小字' },
    { value: 'medium', label: '中字' },
    { value: 'large', label: '大字' },
  ]), [])
  const fontScaleMenu = React.useMemo(() => ({
    items: fontScaleOptions.map((option) => ({
      key: option.value,
      label: option.label,
      disabled: fontScale === option.value,
    })),
    onClick: ({ key }: { key: string }) => {
      if (key === 'small' || key === 'medium' || key === 'large') {
        setFontScale(key)
      }
    },
  }), [fontScale, fontScaleOptions, setFontScale])

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault()
      setInstallPrompt(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  useEffect(() => {
    try {
      const skipKey = 'ebookhub:skip-home-autoresume-once'
      const consumedKey = 'ebookhub:home-autoresume-consumed'
      const skipOnce = sessionStorage.getItem(skipKey) === '1'
      if (skipOnce) {
        try { sessionStorage.removeItem(skipKey) } catch { /* noop */ }
        return
      }
      const consumed = sessionStorage.getItem(consumedKey) === '1'
      if (consumed) return
      const raw = localStorage.getItem('ebookhub:last-reader-session')
      if (!raw) return
      const j = JSON.parse(raw) as { path?: unknown; at?: unknown }
      const path = String(j?.path || '').trim()
      const at = Number(j?.at || 0)
      if (!/^\/reader\/.+/.test(path)) return
      if (Number.isFinite(at) && at > 0 && (Date.now() - at) > 30 * 24 * 3600 * 1000) return
      setResumePath(path)
      setResumeAt(Number.isFinite(at) ? at : 0)
    } catch { /* noop */ }
  }, [])

  const handleContinueReading = () => {
    if (!resumePath) return
    try { sessionStorage.setItem('ebookhub:home-autoresume-consumed', '1') } catch { /* noop */ }
    const path = String(resumePath || '').trim()
    if (!/^\/reader\/.+/.test(path)) return
    navigate(path)
  }

  const handleDismissContinueReading = () => {
    setResumePath('')
    setResumeAt(0)
    try { sessionStorage.setItem('ebookhub:home-autoresume-consumed', '1') } catch { /* noop */ }
  }

  const openBookReader = React.useCallback((bookId: number | string) => {
    const id = String(bookId || '').trim()
    if (!id) return
    navigate(`/reader/${id}`)
  }, [navigate])

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt()
      const { outcome } = await installPrompt.userChoice
      if (outcome === 'accepted') {
        setInstallPrompt(null)
      }
    } else {
      const ua = navigator.userAgent.toLowerCase()
      if (/iphone|ipad|ipod/.test(ua)) {
         Modal.info({
           title: '添加到主屏幕',
           content: (
             <div>
               <p>在 Safari 浏览器中：</p>
               <p>1. 点击底部工具栏的分享图标 <span style={{ fontSize: 20 }}>⎋</span></p>
               <p>2. 向下滚动并选择“添加到主屏幕”</p>
             </div>
           ),
           maskClosable: true,
           okText: '知道了'
         })
      } else if (/android|harmony/i.test(ua)) {
         Modal.info({
           title: '添加到主屏幕',
           content: (
             <div>
               <p>请点击浏览器菜单图标 <span style={{ fontSize: 20 }}>⋮</span></p>
               <p>选择“安装应用”、“添加到主屏幕”或“添加至桌面”</p>
               <p className="text-gray-500 text-xs mt-2">适用于安卓、鸿蒙系统及各类国产浏览器</p>
             </div>
           ),
           maskClosable: true,
           okText: '知道了'
         })
      } else if (/windows|macintosh|linux/i.test(ua)) {
         Modal.info({
           title: '安装到桌面',
           content: (
             <div>
               <p>请点击浏览器地址栏右侧的 <span style={{ fontSize: 20 }}>⊕</span> 或 <span style={{ fontSize: 20 }}>⬇</span> 安装图标</p>
               <p>或在菜单中选择“安装 {siteName}”</p>
             </div>
           ),
           maskClosable: true,
           okText: '知道了'
         })
      } else {
         message.info('请使用浏览器的“安装应用”或“添加到主屏幕”功能')
      }
    }
  }

  useEffect(() => {
    const refresh = async () => {
      try {
        const res = await request.get('/api/auth/public-config', { headers: { 'X-Skip-401-Redirect': '1', 'X-Silent': '1' } })
        if (res.data?.success) {
          setSiteName(res.data.data.site_name || '伴读么 - Ai角色朗读电子书屋')
          setMarqueeContent(res.data.data.marquee_content || '')
          setRecentImportVisible(res.data.data.recent_import_visible === true)
        }
      } catch {
        setRecentImportVisible(false)
      }

      if (!isAuthenticated) {
        setCanUpload(false)
        return
      }

      try {
        const res = await request.get('/api/upload/settings', { headers: { 'X-Skip-401-Redirect': '1', 'X-Silent': '1' } })
        const data = res.data?.data || {}
        setCanUpload(Boolean(data.canUpload))
      } catch { setCanUpload(false) }
    }
    const onCfg = () => { refresh() }
    const onPerm = () => { refresh() }
    refresh()
    window.addEventListener('ebookhub:config-changed', onCfg as unknown as EventListener)
    window.addEventListener('ebookhub:permission-changed', onPerm as unknown as EventListener)
    return () => {
      window.removeEventListener('ebookhub:config-changed', onCfg as unknown as EventListener)
      window.removeEventListener('ebookhub:permission-changed', onPerm as unknown as EventListener)
    }
  }, [isAuthenticated, user?.id])
  
  const fetchRecentBooks = async () => {
    try {
      setRecentBooksError('')
      const res = await request.get('/api/admin/recent-books', {
        params: { page: 1, pageSize: 50 },
        headers: { 'X-Skip-401-Redirect': '1' }
      })
      
      if (res.data?.success && res.data?.data) {
        setRecentBooks(Array.isArray(res.data.data) ? res.data.data : [])
      } else {
        setRecentBooks([])
        setRecentBooksError('最近导入加载失败，请稍后重试')
      }
    } catch {
      setRecentBooks([])
      setRecentBooksError('网络异常，请稍后重试')
    }
  }
  
  useEffect(() => {
    (async () => {
      try {
        // 优先尝试公共统计接口，它对所有用户开放且包含今日新增数据
        const res = await request.get('/api/books/stats/public', { headers: { 'X-Skip-401-Redirect': '1', 'X-Silent': '1' } })
        if (res.data?.success && res.data?.data) {
          setStats({ 
            books: Number(res.data.data.totalBooks || 0), 
            today: Number(res.data.data.todayBooks || 0) 
          })
          return
        }

        // 如果公共接口失败，降级到旧逻辑（仅管理员尝试admin接口）
        if (isAuthenticated && (role === 'admin' || role === 'super_admin')) {
          try {
            const res = await request.get('/api/admin/stats', { headers: { 'X-Skip-401-Redirect': '1' } })
            const data = res.data?.data || {}
            setStats({ books: Number(data?.totalBooks || 0), today: Number((data?.todayUploads ?? data?.todayBooks) || 0) })
          } catch (adminError) {
            const res = await request.get('/api/search?query=&page=1&limit=1', { headers: { 'X-Silent': '1', 'X-Skip-401-Redirect': '1' } })
            const data = res.data?.data || {}
            setStats({ books: Number(data?.pagination?.total || 0), today: 0 })
          }
        } else {
          const res = await request.get('/api/search?query=&page=1&limit=1', { headers: { 'X-Silent': '1' } })
          const data = res.data?.data || {}
          setStats({ books: Number(data?.pagination?.total || 0), today: 0 })
        }
      } catch {
        setStats({ books: 0, today: 0 })
      }
    })()
    
    if (canViewRecentImports) {
      fetchRecentBooks()
    } else {
      setRecentBooks([])
      setRecentBooksError('')
    }
    (async () => {
      try {
        const res = await request.get('/api/books/recommendations/public?limit=200', { headers: { 'X-Skip-401-Redirect': '1', 'X-Silent': '1' } })
        const items = res.data?.data?.items || []
        setPublicRecs(items)
        setPublicRecsPage(1)
        setHotCount(Array.isArray(items) ? items.length : 0)
      } catch { setPublicRecs([]); setHotCount(0) }
    })()
  }, [canViewRecentImports, isAuthenticated, role])

  useEffect(() => {
    if (!marqueeContent) return
    if (typeof window === 'undefined') return
    let raf = 0
    let ro: ResizeObserver | null = null
    const calc = () => {
      const container = marqueeContainerRef.current
      const measure = marqueeMeasureRef.current
      if (!container || !measure) return
      const containerW = Math.max(1, Math.floor(container.clientWidth || 1))
      const contentW = Math.max(1, Math.ceil(measure.scrollWidth || measure.getBoundingClientRect().width || 1))
      const gap = contentW < containerW ? 24 : 64
      const one = contentW + gap
      const need = Math.ceil(containerW / one) + 2
      const repeat = Math.max(2, Math.min(10, need))
      const speed = contentW < containerW ? 36 : 54
      const duration = Math.max(12, Math.min(45, Math.round(one / speed)))
      setMarqueeGapPx(gap)
      setMarqueeShiftPx(one)
      setMarqueeRepeat(repeat)
      setMarqueeDurationSec(duration)
    }
    raf = window.requestAnimationFrame(calc)
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        if (raf) window.cancelAnimationFrame(raf)
        raf = window.requestAnimationFrame(calc)
      })
      if (marqueeContainerRef.current) ro.observe(marqueeContainerRef.current)
    }
    return () => {
      if (raf) window.cancelAnimationFrame(raf)
      if (ro) ro.disconnect()
    }
  }, [marqueeContent])

  const pagedPublicRecs = React.useMemo(() => {
    const list = Array.isArray(publicRecs) ? publicRecs : []
    const ps = Math.max(1, Number(publicRecsPageSize || 24))
    const p = Math.max(1, Number(publicRecsPage || 1))
    const start = (p - 1) * ps
    return list.slice(start, start + ps)
  }, [publicRecs, publicRecsPage, publicRecsPageSize])
  const totalPublicRecPages = React.useMemo(() => {
    return Math.max(1, Math.ceil(Math.max(0, publicRecs.length) / Math.max(1, publicRecsPageSize)))
  }, [publicRecs.length, publicRecsPageSize])

  const displayedContinueReadingItems = React.useMemo(() => {
    if (continueReadingItems.length > 0) return continueReadingItems
    return buildFallbackContinueReadingItems(publicRecs).slice(0, 12)
  }, [continueReadingItems, publicRecs])

  const effectiveContinueReadingFallback = continueReadingItems.length > 0
    ? continueReadingFallback
    : true

  const loadContinueReading = React.useCallback(async (targetPage: number, append: boolean) => {
    if (!isAuthenticated || !user?.id) {
      setContinueReadingItems([])
      setContinueReadingPage(0)
      setContinueReadingHasMore(false)
      setContinueReadingFallback(true)
      return
    }
    if (append) {
      setContinueReadingLoadingMore(true)
    } else {
      setContinueReadingLoading(true)
    }
    try {
      const data = await getContinueReadingPage(targetPage)
      const incoming = Array.isArray(data.items) ? data.items : []
      setContinueReadingItems((prev) => {
        const next = append ? [...prev, ...incoming] : incoming
        const seen = new Set<number>()
        return next.filter((item) => {
          const bookId = Number(item.book_id || 0)
          if (!(bookId > 0) || seen.has(bookId)) return false
          seen.add(bookId)
          return true
        }).slice(0, 30)
      })
      setContinueReadingPage(Number(data.page_num || targetPage))
      setContinueReadingHasMore(Boolean(data.has_more))
      setContinueReadingFallback(Boolean(data.fallback))
    } catch {
      if (!append) {
        setContinueReadingItems([])
        setContinueReadingPage(0)
        setContinueReadingHasMore(false)
        setContinueReadingFallback(true)
      }
    } finally {
      setContinueReadingLoading(false)
      setContinueReadingLoadingMore(false)
    }
  }, [isAuthenticated, user?.id])

  const loadInitialContinueReading = React.useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      setContinueReadingItems([])
      setContinueReadingPage(0)
      setContinueReadingHasMore(false)
      setContinueReadingFallback(true)
      return
    }
    setContinueReadingLoading(true)
    try {
      const [page1, page2] = await Promise.all([
        getContinueReadingPage(1),
        getContinueReadingPage(2),
      ])
      const merged = [...(Array.isArray(page1.items) ? page1.items : []), ...(Array.isArray(page2.items) ? page2.items : [])]
      const seen = new Set<number>()
      const deduped = merged.filter((item) => {
        const bookId = Number(item.book_id || 0)
        if (!(bookId > 0) || seen.has(bookId)) return false
        seen.add(bookId)
        return true
      }).slice(0, 12)
      setContinueReadingItems(deduped)
      setContinueReadingPage(deduped.length > 6 ? 2 : 1)
      setContinueReadingHasMore(Boolean(page2.has_more))
      setContinueReadingFallback(Boolean(page1.fallback) && Boolean(page2.fallback))
    } catch {
      setContinueReadingItems([])
      setContinueReadingPage(0)
      setContinueReadingHasMore(false)
      setContinueReadingFallback(true)
    } finally {
      setContinueReadingLoading(false)
    }
  }, [isAuthenticated, user?.id])

  useEffect(() => {
    void loadInitialContinueReading()
  }, [loadInitialContinueReading])

  const handleContinueReadingMore = React.useCallback(() => {
    if (continueReadingLoadingMore || continueReadingLoading || !continueReadingHasMore) return
    const nextPage = continueReadingPage + 1
    if (nextPage > 5) return
    void loadContinueReading(nextPage, true)
  }, [continueReadingHasMore, continueReadingLoading, continueReadingLoadingMore, continueReadingPage, loadContinueReading])

  const handleContinueReadingCardClick = React.useCallback(async (item: ContinueReadingItem, index: number) => {
    const position = index + 1
    if (isAuthenticated) {
      try {
        await trackContinueReadingClick({ book_id: Number(item.book_id), position })
      } catch { /* noop */ }
    }
    openBookReader(item.book_id)
  }, [isAuthenticated, openBookReader])

  const handleShareSite = async () => {
    if (sharingSite) return
    try {
      setSharingSite(true)
      const payload = buildSiteSharePayload({
        siteName,
        tagline: '全格式电子书管理与阅读平台'
      })
      await shareContent(payload)
    } catch {
      message.error('分享失败')
    } finally {
      setSharingSite(false)
    }
  }

  useEffect(() => {
    setPublicRecsPage(1)
    setPublicRecsPageSize((prev) => {
      if (isMobile) return 12
      return prev === 12 ? 24 : prev
    })
  }, [isMobile])

  useEffect(() => {
    setPublicRecsPage((prev) => Math.min(Math.max(1, prev), totalPublicRecPages))
  }, [totalPublicRecPages])

  const handlePublicRecPageChange = React.useCallback((page: number, pageSize?: number) => {
    setPublicRecsPage(page)
    if (typeof pageSize === 'number' && pageSize !== publicRecsPageSize) {
      setPublicRecsPageSize(pageSize)
    }
  }, [publicRecsPageSize])

  const handleRecommendationTouchStart = React.useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile) return
    const touch = event.touches[0]
    if (!touch) return
    recommendationSwipeRef.current = { x: touch.clientX, y: touch.clientY }
  }, [isMobile])

  const handleRecommendationTouchEnd = React.useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile || totalPublicRecPages <= 1) return
    const start = recommendationSwipeRef.current
    const touch = event.changedTouches[0]
    recommendationSwipeRef.current = null
    if (!start || !touch) return
    const dx = touch.clientX - start.x
    const dy = touch.clientY - start.y
    if (Math.abs(dx) < 56 || Math.abs(dx) <= Math.abs(dy) * 1.2) return
    if (dx < 0 && publicRecsPage < totalPublicRecPages) {
      setPublicRecsPage((prev) => Math.min(totalPublicRecPages, prev + 1))
      return
    }
    if (dx > 0 && publicRecsPage > 1) {
      setPublicRecsPage((prev) => Math.max(1, prev - 1))
    }
  }, [isMobile, publicRecsPage, totalPublicRecPages])

  return (
    <div className="space-y-4 md:space-y-6">
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(calc(var(--marquee-shift, 0px) * -1)); }
        }
        .marquee-container {
          white-space: nowrap;
        }
        .animate-marquee {
          display: flex;
          width: fit-content;
          animation: marquee var(--marquee-duration, 30s) linear infinite;
          will-change: transform;
        }
        .animate-marquee > div {
          flex: 0 0 auto;
          white-space: nowrap;
          padding-right: var(--marquee-gap, 64px);
        }
        .marquee-container:hover .animate-marquee {
          animation-play-state: paused;
        }
      `}</style>
      <div className="rounded-[20px] md:rounded-2xl bg-gradient-to-br from-orange-100 via-amber-50 to-white p-3 md:p-6 shadow-warm border border-orange-100 relative overflow-hidden group">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-orange-200/40 via-transparent to-transparent opacity-60" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-300/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
        
        <div className="relative z-10 flex flex-col gap-4 md:gap-6">
          <div className="min-w-0">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <h1 className="text-[1.85rem] leading-tight md:text-4xl font-bold text-gray-900 tracking-tight flex items-center gap-3 min-w-0">
                <span className="text-amber-700 break-words">
                  {siteName}
                </span>
              </h1>
              <div className="shrink-0 flex items-center justify-end gap-1 self-end">
                {isMobile ? (
                  <Dropdown menu={fontScaleMenu} trigger={['click']} placement="bottomRight">
                    <Button
                      size="small"
                      type="default"
                      className="h-8 w-8 min-w-0 px-0 flex items-center justify-center rounded-lg border border-gray-200/90 bg-white/90 text-gray-500 shadow-sm hover:border-primary-300 hover:text-primary-600 hover:bg-white"
                      aria-label="字体大小"
                      title="字体大小"
                    >
                      <span className="text-[13px] font-semibold leading-none">T</span>
                    </Button>
                  </Dropdown>
                ) : (
                  <div className="flex flex-wrap items-center justify-end gap-1.5 rounded-xl border border-white/70 bg-white/65 px-2 py-1 shadow-sm backdrop-blur-sm">
                    {fontScaleOptions.map((option) => (
                      <Button
                        key={option.value}
                        size="small"
                        type={fontScale === option.value ? 'primary' : 'default'}
                        className="min-w-[52px] rounded-lg"
                        onClick={() => setFontScale(option.value)}
                      >
                        {option.label}
                      </Button>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-end gap-1">
                  <Button
                    size="small"
                    type="text"
                    className="h-8 w-8 md:h-9 md:w-9 flex items-center justify-center rounded-lg text-gray-600 hover:text-primary-600 hover:bg-white/70"
                    icon={<AppstoreAddOutlined />}
                    onClick={handleInstall}
                    aria-label="桌面快捷"
                    title="桌面快捷"
                  />
                  <Button
                    size="small"
                    type="text"
                    className="h-8 w-8 md:h-9 md:w-9 flex items-center justify-center rounded-lg text-gray-600 hover:text-primary-600 hover:bg-white/70"
                    icon={<ShareAltOutlined />}
                    onClick={handleShareSite}
                    loading={sharingSite}
                    aria-label="分享网站"
                    title="分享网站"
                  />
                </div>
              </div>
            </div>
            <p className="mt-2 text-sm md:text-lg text-gray-600 font-medium max-w-xl">
              全格式支持 · 去重与检索 · 多终端阅读同步
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 md:flex md:flex-wrap md:gap-3 w-full">
            {canUpload && (
              <Button type="primary" size="large" className="btn-gradient h-9 md:h-11 px-3 md:px-6 rounded-xl w-full md:w-auto md:min-w-[124px] hover:scale-[1.02] transition-transform" onClick={() => navigate('/import')}>
                立即导入
              </Button>
            )}
            <Button size="large" className="h-9 md:h-11 px-3 md:px-6 rounded-xl border-gray-200 text-gray-700 w-full md:w-auto md:min-w-[124px] hover:border-primary-400 hover:text-primary-600 bg-white shadow-sm" onClick={() => navigate('/search')}>
              浏览书库
            </Button>
            <Button size="large" className="h-9 md:h-11 px-3 md:px-6 rounded-xl border-gray-200 text-gray-700 w-full md:w-auto md:min-w-[124px] hover:border-primary-400 hover:text-primary-600 bg-white shadow-sm" onClick={() => navigate('/tts')}>
              文本转语音
            </Button>
            {isAuthenticated ? (
              <Button size="large" className="h-9 md:h-11 px-3 md:px-6 rounded-xl border-gray-200 text-gray-700 w-full md:w-auto md:min-w-[124px] hover:border-primary-400 hover:text-primary-600 bg-white shadow-sm" onClick={() => navigate((user?.role === 'admin' || user?.role === 'super_admin') ? '/admin' : '/profile')}>
                {(user?.role === 'admin' || user?.role === 'super_admin') ? '后台管理' : '个人中心'}
              </Button>
            ) : (
              <Button size="large" className="h-9 md:h-11 px-3 md:px-6 rounded-xl border-gray-200 text-gray-700 w-full md:w-auto md:min-w-[124px] hover:border-primary-400 hover:text-primary-600 bg-white shadow-sm" onClick={() => navigate('/login')}>
                登录/注册
              </Button>
            )}
          </div>
        </div>
      </div>
      {resumePath ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2.5 md:px-4 md:py-3 flex items-center justify-between gap-3">
          <div className="text-sm text-amber-800">
            发现你上次的阅读进度
            {resumeAt > 0 ? `（${new Date(resumeAt).toLocaleString()}）` : ''}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="small" type="primary" onClick={handleContinueReading}>
              继续阅读
            </Button>
            <Button size="small" onClick={handleDismissContinueReading}>
              暂不继续
            </Button>
          </div>
        </div>
      ) : null}
      
      {marqueeContent && (
        <div
          ref={marqueeContainerRef}
          className="bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 text-orange-600 font-normal px-3 py-2.5 md:px-4 md:py-3 rounded-lg border border-orange-100 overflow-hidden relative marquee-container flex items-center text-sm shadow-sm"
        >
          <span
            ref={marqueeMeasureRef}
            style={{
              position: 'absolute',
              visibility: 'hidden',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              left: -99999,
              top: -99999,
            }}
          >
            {marqueeContent}
          </span>
          <div
            className="animate-marquee"
            style={{
              ['--marquee-shift' as never]: `${marqueeShiftPx || 0}px`,
              ['--marquee-duration' as never]: `${marqueeDurationSec || 30}s`,
              ['--marquee-gap' as never]: `${marqueeGapPx || 64}px`,
            }}
          >
            {Array.from({ length: marqueeRepeat }, (_, i) => (
              <div key={i}>{marqueeContent}</div>
            ))}
          </div>
        </div>
      )}

      <Row gutter={isMobile ? [12, 12] : [16, 16]}>
        <Col xs={8} sm={8} md={8}>
          <Card size="small" className="theme-card border-none" styles={{ body: { padding: '8px 12px' } }}>
            <Statistic
              title={<span className="inline-flex items-center gap-2 text-gray-500"><BookOutlined className="text-primary-500" />册数</span>}
              value={stats.books}
              valueStyle={{ fontSize: 18, lineHeight: 1.1, fontWeight: 600, color: 'var(--color-text-main)' }}
            />
          </Card>
        </Col>
        <Col xs={8} sm={8} md={8}>
          <Card size="small" className="theme-card border-none" styles={{ body: { padding: '8px 12px' } }}>
            <Statistic
              title={<span className="inline-flex items-center gap-2 text-gray-500"><ImportOutlined className="text-primary-500" />新增</span>}
              value={stats.today}
              valueStyle={{ fontSize: 18, lineHeight: 1.1, fontWeight: 600, color: 'var(--color-text-main)' }}
            />
          </Card>
        </Col>
        <Col xs={8} sm={8} md={8}>
          <Card size="small" className="theme-card border-none" styles={{ body: { padding: '8px 12px' } }}>
            <Statistic
              title={<span className="inline-flex items-center gap-2 text-gray-500"><SearchOutlined className="text-primary-500" />热榜</span>}
              value={hotCount}
              valueStyle={{ fontSize: 18, lineHeight: 1.1, fontWeight: 600, color: 'var(--color-text-main)' }}
            />
          </Card>
        </Col>
      </Row>
      <Row gutter={isMobile ? [16, 16] : [24, 24]}>
        <Col xs={24} md={24}>
          <Card
            title={<span className="text-lg font-bold text-gray-800">推荐榜</span>}
            className="theme-card home-section-card home-section-card--recommend border-none mb-4 md:mb-6"
            styles={{ header: { borderBottom: '1px solid var(--color-primary-50)' } }}
          >
            {publicRecs.length > 0 ? (
              <div
                className="recommendation-swipe-surface"
                onTouchStart={handleRecommendationTouchStart}
                onTouchEnd={handleRecommendationTouchEnd}
                style={isMobile ? { touchAction: 'pan-y' } : undefined}
              >
                {isMobile ? (
                  <div className="mb-2.5 flex items-center justify-between gap-2 rounded-xl border border-blue-100 bg-blue-50/70 px-2.5 py-1.5 text-[11px] leading-5 text-blue-700">
                    <span>左右滑动可切换分页</span>
                    <span>{publicRecsPage}/{totalPublicRecPages} 页</span>
                  </div>
                ) : null}
                <Row gutter={[16, 16]}>
                  {pagedPublicRecs.map((item, idx) => (
                    <Col xs={12} sm={8} md={6} lg={6} xl={4} key={`${item.source}-${item.id}`}>
                      <HomeBookCard
                        id={item.id}
                        title={item.name}
                        author={item.author}
                        cover={getBookCoverUrl(item.id)}
                        format={item.format}
                        avgRating={item.source === 'rating' ? Number(item.avg_rating || 0) : 0}
                        ratingCount={Number(item.rating_count || 0)}
                        badgeText={`#${(publicRecsPage - 1) * publicRecsPageSize + idx + 1}`}
                        tagText={item.source === 'strong' ? '强推荐' : undefined}
                        description={item.source === 'strong' ? '编辑精选强推荐内容' : '评分热度表现突出'}
                        onClick={() => openBookReader(item.id)}
                      />
                    </Col>
                  ))}
                </Row>
                <div className={`mt-2.5 md:mt-4 flex ${isMobile ? 'justify-center' : 'justify-end'}`}>
                  <Pagination
                    current={publicRecsPage}
                    pageSize={publicRecsPageSize}
                    total={publicRecs.length}
                    showSizeChanger={!isMobile}
                    pageSizeOptions={isMobile ? [12] : [12, 24, 48, 96]}
                    showQuickJumper={!isMobile}
                    size={isMobile ? 'small' : 'default'}
                    simple={isMobile}
                    showLessItems={isMobile}
                    responsive
                    onChange={handlePublicRecPageChange}
                  />
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-12 bg-gray-50 rounded-lg">暂无推荐</div>
            )}
          </Card>
        </Col>
      </Row>

      <ContinueReadingSection
        items={displayedContinueReadingItems}
        loading={continueReadingLoading}
        loadingMore={continueReadingLoadingMore}
        hasMore={continueReadingHasMore}
        usingFallback={effectiveContinueReadingFallback}
        onLoadMore={handleContinueReadingMore}
        onBookClick={handleContinueReadingCardClick}
        onDiscoverMore={() => navigate('/search')}
      />

      <CategoryExhibitionSection
        onBookClick={(item) => openBookReader(item.book_id)}
        onDiscoverMore={() => navigate('/search')}
      />

      {canViewRecentImports && (
      <Card
        title={<span className="text-lg font-bold text-gray-800">最近导入</span>}
        className="theme-card home-section-card home-section-card--recent border-none mb-4 md:mb-6"
        styles={{ header: { borderBottom: '1px solid var(--color-primary-50)' } }}
      >
        <div className="flex items-center gap-2 mb-4">
          <Select
            size="small"
            value={sortField}
            onChange={(v) => setSortField(v as 'title' | 'author' | 'format' | 'size' | 'uploadTime')}
            options={[
              { value: 'title', label: '书名' },
              { value: 'author', label: '作者' },
              { value: 'format', label: '格式' },
              { value: 'size', label: '大小' },
              { value: 'uploadTime', label: '上传时间' },
            ]}
            style={{ width: 120 }}
          />
          <Select
            size="small"
            value={sortOrder}
            onChange={(v) => setSortOrder(v as 'ascend' | 'descend')}
            options={[{ value: 'ascend', label: '升序' }, { value: 'descend', label: '降序' }]}
            style={{ width: 100 }}
          />
        </div>
        <div style={{ maxHeight: 10 * 64, overflowY: 'auto' }} className="space-y-2 pr-2 custom-scrollbar">
          {recentBooksError ? (
            <HomeSectionState
              type="error"
              title="网络异常"
              description={recentBooksError}
              actionLabel="重新加载"
              onAction={() => { void fetchRecentBooks() }}
            />
          ) : sortedRecentBooks.length > 0 ? (
            sortedRecentBooks.map((book) => (
              <div key={book.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-primary-50 hover:shadow-sm transition-all cursor-pointer border border-transparent hover:border-primary-100" onClick={() => openBookReader(book.id)}>
                <div>
                  <h4 className="font-medium text-gray-800">{book.title} <span className="ml-2 text-xs text-gray-500">ID: {book.id}</span> <span className="ml-2 text-xs text-gray-500">{book.author || '未知作者'}</span></h4>
                  <p className="text-xs md:text-sm text-gray-500 mt-1 flex items-center gap-2">
                    <span className="bg-white px-1.5 py-0.5 rounded text-xs border border-gray-200">{book.format}</span>
                    <span>{book.size}</span>
                  </p>
                </div>
                <span className="text-xs md:text-sm text-gray-400">{book.uploadTime}</span>
              </div>
            ))
          ) : (
            <HomeSectionState
              type="empty"
              title="暂无最近导入"
              description="最近导入区域暂时没有可展示的数据"
            />
          )}
        </div>
      </Card>
      )}

      <footer className="pt-2 pb-6 text-center text-xs md:text-sm text-gray-400 leading-relaxed">
        <div className="px-3">
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
            <span>© 2026-现在 Bandume.cn 伴读么 版权所有</span>
            <a
              href="https://beian.miit.gov.cn/"
              target="_blank"
              rel="noreferrer"
              className="text-gray-400 hover:text-gray-500 underline underline-offset-2 decoration-gray-300"
            >
              苏ICP备2025159326号-3
            </a>
            <button
              type="button"
              onClick={() => setAuthorClaimOpen(true)}
              className="font-bold text-gray-400 hover:text-gray-500"
              style={{ background: 'transparent', border: 'none', padding: 0, lineHeight: 'inherit' }}
            >
              作者申报
            </button>
          </div>
          <div className="mt-1">
            建议您使用Edge、Chrome 80+、FireFox 86+、360极速模式等主流浏览器浏览本站
          </div>
        </div>
      </footer>
      <AuthorClaimModal open={authorClaimOpen} onClose={() => setAuthorClaimOpen(false)} />
    </div>
  )
}

export default Home
